import React, { PureComponent } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import classNames from "classnames";
import { PROGRESSIVE_STEP__FIELDS } from "containers/MultistepForm/constants/collections";
import {
  normalizeCardNumber,
  normalizeCVV,
  onlyNumbers,
} from "utils/normalize";
import { Field, reduxForm, formValueSelector } from "redux-form";
import {
  isYearEqual4,
  maxLength15,
  minLength2,
  required,
  validateCreditCard,
  validateCVV,
} from "utils/validations";
import { TextField } from "components/Fields";
import {
  renderMonthField,
  renderYearSelectField,
} from "../../../Step2/input_helpers";
import { Stack } from "@mui/material";

const selector = formValueSelector("fypn_card_form");

const CARD_NAMES = {
  visa: "Visa",
  mc: "Mastercard",
  amex: "American Express",
  discover: "Discover",
};

class Form extends PureComponent {
  componentDidMount() {
    this.checkInitialValidity();
  }

  checkInitialValidity = () => {
    const { additionalData, onDataChange } = this.props;
    const valid = !!(
      additionalData.firstName &&
      additionalData.lastName &&
      additionalData.creditCardNumber &&
      additionalData.expMonth &&
      additionalData.expYear &&
      additionalData.securityCode &&
      additionalData.billingZip
    );
    onDataChange({
      data: additionalData,
      valid: valid,
    });
  };

  handleChange = () => {
    const { additionalData, onDataChange, valid } = this.props || {};
    onDataChange({
      data: additionalData,
      valid: valid,
    });
  };

  detectCardType = (digits) => {
    if (!digits) return null;
    if (/^4/.test(digits)) return "visa";
    if (/^5[1-5]/.test(digits)) return "mc";
    if (/^2[2-7]/.test(digits)) return "mc";
    if (/^3[47]/.test(digits)) return "amex";
    if (/^6(?:011|5)/.test(digits)) return "discover";
    return null;
  };

  renderField = ({ input, meta }) => {
    const field = input.name;
    const fieldConfig = PROGRESSIVE_STEP__FIELDS[field] || {};
    return (
      <TextField
        type={"text"}
        label={fieldConfig.label || field}
        placeholder={fieldConfig.placeholder || ""}
        meta={meta}
        customChange={this.handleChange}
        {...input}
      />
    );
  };

  renderCardNumberField = ({ input, meta }) => {
    const digits = (input.value || "").replace(/\D/g, "");
    const cardType = this.detectCardType(digits);

    return (
      <div className="fypn-card-form__card-number-wrap">
        <div
          className={classNames("fypn-card-form__card-input-icon", {
            "fypn-card-form__card-input-icon--visa": cardType === "visa",
            "fypn-card-form__card-input-icon--mc": cardType === "mc",
            "fypn-card-form__card-input-icon--amex": cardType === "amex",
            "fypn-card-form__card-input-icon--discover": cardType === "discover",
          })}
        >
          <svg
            width="16"
            height="11"
            viewBox="0 0 24 18"
            fill="none"
            stroke="white"
            strokeWidth="1.5"
          >
            <rect x="1" y="1" width="22" height="16" rx="2" />
            <line x1="1" y1="6" x2="23" y2="6" />
          </svg>
        </div>
        <TextField
          type={"text"}
          label={"Credit Card Number *"}
          placeholder={"0000 0000 0000 0000"}
          meta={meta}
          customChange={this.handleChange}
          inputMode="numeric"
          autoComplete="cc-number"
          {...input}
        />
        <div
          className={classNames("fypn-card-form__card-detected", {
            "fypn-card-form__card-detected--visible": !!cardType,
          })}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span>{cardType ? `${CARD_NAMES[cardType]} card detected` : ""}</span>
        </div>
      </div>
    );
  };

  renderBillingZip = ({ input, meta }) => (
    <TextField
      type={"text"}
      label={"Billing ZIP *"}
      placeholder={"ZIP"}
      meta={meta}
      customChange={this.handleChange}
      {...input}
    />
  );

  render() {
    return (
      <div className="fypn-card-form__form">
        <div className="fypn-card-form__section">
          <div className="fypn-card-form__section-header">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2563eb"
              strokeWidth="2"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            <span className="fypn-card-form__section-title">Cardholder Name</span>
          </div>

          <div className="fypn-card-form__field-row">
            <div className="fypn-card-form__field">
              <Field
                name="firstName"
                validate={[required, minLength2, maxLength15]}
                component={this.renderField}
              />
            </div>
            <div className="fypn-card-form__field">
              <Field
                name="lastName"
                validate={[required, minLength2, maxLength15]}
                component={this.renderField}
              />
            </div>
          </div>
        </div>

        <div className="fypn-card-form__section">
          <div className="fypn-card-form__section-header">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#2563eb"
              strokeWidth="2"
            >
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
              <line x1="1" y1="10" x2="23" y2="10" />
            </svg>
            <span className="fypn-card-form__section-title">Card Details</span>
          </div>

          <div className="fypn-card-form__field">
            <Field
              name="creditCardNumber"
              normalize={normalizeCardNumber}
              validate={[required, validateCreditCard]}
              component={this.renderCardNumberField}
            />
          </div>

          <div className="fypn-card-form__field-row">
            <div className="fypn-card-form__field">
              <div className="text-field-component flex flex--column">
                <label className="font-uppercased text-field-label">
                  Exp. Date *
                </label>
                <Stack
                  direction="row"
                  sx={{ gap: 2, "& > div": { width: "100%" } }}
                >
                  <Field
                    name="expMonth"
                    validate={[required]}
                    component={renderMonthField}
                    normalize={onlyNumbers}
                  />
                  <Field
                    name="expYear"
                    validate={[required, isYearEqual4]}
                    component={renderYearSelectField}
                    normalize={onlyNumbers}
                  />
                </Stack>
              </div>
            </div>
            <div className="fypn-card-form__field">
              <Field
                name="securityCode"
                normalize={normalizeCVV}
                validate={[required, validateCVV]}
                component={this.renderField}
              />
            </div>
          </div>

          <div className="fypn-card-form__field">
            <Field
              name="billingZip"
              normalize={onlyNumbers}
              validate={[required]}
              component={this.renderBillingZip}
            />
          </div>
        </div>
      </div>
    );
  }
}

Form.propTypes = {
  onDataChange: PropTypes.func,
};

Form.defaultProps = {
  onDataChange: () => {},
};

Form = reduxForm({
  form: "fypn_card_form",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(Form);

Form = connect((state) => ({
  additionalData: selector(
    state,
    "firstName",
    "lastName",
    "creditCardNumber",
    "expMonth",
    "expYear",
    "securityCode",
    "billingZip"
  ),
}))(Form);

export default Form;
